import {
  Banner,
  BannerDescription,
  Box,
  Button,
  ButtonGroup,
  FormattedDateTime,
  Link,
  List,
  ListItem,
} from '@customerio/pluma-components/react';
import { useDeliveryRelatedModel, useDeliveryRowPermission } from '@/features/deliveries';
import type { Delivery, DeliveryDetail } from '@/services/db/delivery/entity';
import { useNewsletterOptional } from '@/services/db/newsletter/queries';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { generatePath } from '@/services/router/generate-path';
import { useRouter } from '@tanstack/react-router';
import { isEmptyString } from '@/utils/string';
import {
  deliveryChannelShortName,
  hasDeliveryAction,
  hasDeliveryCustomer,
  resolveDeliveryAlertState,
  type DeliveryAlertState,
} from '../utils/alert-state';
import * as styles from './delivery-detail-alert.css';

type DeliveryDetailAlertProps = { delivery: Delivery; detail: DeliveryDetail };

/**
 * The delivery-detail alert: a scheduled/ready/paused/bounce/error banner above
 * the message. Read-only users see the error-list action buttons disabled; the
 * newsletter's paused state gates the paused banner.
 */
export function DeliveryDetailAlert({ delivery, detail }: DeliveryDetailAlertProps) {
  const workspaceId = requireWorkspaceId();
  // `isLoading` folds into read-only until the parent model resolves: an
  // api-triggered broadcast reads as `kind: 'none'` mid-flight, so the permission
  // gate would fall back to the wrong (campaigns) check and briefly enable the
  // action buttons — same gate the preview header applies.
  const { model, isLoading } = useDeliveryRelatedModel(delivery);
  const { hasReadOnlyPermission } = useDeliveryRowPermission(delivery, model);

  const newsletterId = delivery.newsletter_id;
  const newsletterQuery = useNewsletterOptional({
    workspaceId,
    id: newsletterId != null ? String(newsletterId) : '',
    enabled: newsletterId != null,
  });

  // The paused banner depends on the newsletter's paused flag; hold the alert
  // until that lookup settles so a paused delivery doesn't first flash "ready".
  if (newsletterId != null && newsletterQuery.isLoading) {
    return null;
  }

  const alert = resolveDeliveryAlertState(delivery, detail, {
    newsletterIsPaused: newsletterQuery.data?.is_paused === true,
  });

  return <AlertBanner delivery={delivery} alert={alert} hasReadOnlyPermission={hasReadOnlyPermission || isLoading} />;
}

type AlertBannerProps = {
  delivery: Delivery;
  alert: DeliveryAlertState;
  hasReadOnlyPermission: boolean;
};

function AlertBanner({ delivery, alert, hasReadOnlyPermission }: AlertBannerProps) {
  const taxonomy = useProductTaxonomy();

  switch (alert.kind) {
    case 'none':
      return null;
    case 'paused': {
      const newsletterTerm = taxonomy.term('broadcasts.singular');
      return (
        <Banner variant="caution" icon="pause" title={`Your ${newsletterTerm} is paused.`}>
          <BannerDescription>Sending was paused on this {newsletterTerm}, but may still be resumed.</BannerDescription>
        </Banner>
      );
    }
    case 'scheduled':
      return (
        <Banner variant="success" icon={null}>
          <BannerDescription>
            This message is scheduled to send: <FormattedDateTime date={alert.sendAt} format="medium_with_time" />.
          </BannerDescription>
        </Banner>
      );
    case 'ready':
      return (
        <Banner variant="success" icon={null}>
          <BannerDescription>This message doesn&apos;t have any errors. It is ready to send.</BannerDescription>
        </Banner>
      );
    case 'reason':
      return <ReasonBanner alert={alert} />;
    case 'errorList':
      return <ErrorListBanner delivery={delivery} alert={alert} hasReadOnlyPermission={hasReadOnlyPermission} />;
    case 'error':
      return (
        <Banner variant="error" icon={null}>
          <BannerDescription>{alert.message}.</BannerDescription>
        </Banner>
      );
    default: {
      const _exhaustive: never = alert;
      return _exhaustive;
    }
  }
}

type ReasonBannerProps = { alert: Extract<DeliveryAlertState, { kind: 'reason' }> };

function ReasonBanner({ alert }: ReasonBannerProps) {
  const workspaceId = requireWorkspaceId();
  const router = useRouter();
  const suppressionHref =
    alert.suppressionEmail == null
      ? null
      : router.buildLocation({
          to: '/workspaces/$workspaceId/settings/actions/email/suppression_list',
          params: { workspaceId },
          search: { email: alert.suppressionEmail },
        }).href;

  return (
    <Banner variant="information" icon={null} className={styles.reasonBanner}>
      <BannerDescription>
        {/* The reason often echoes recipient PII (email/attributes) — keep it out of FullStory. */}
        <Box as="span" className="fs-mask">
          {alert.message}
        </Box>
        .
        {suppressionHref != null ? (
          <>
            {' '}
            Search the{' '}
            <Link href={suppressionHref} variant="secondary">
              delivery provider suppression list
            </Link>{' '}
            for this email.
          </>
        ) : null}
      </BannerDescription>
    </Banner>
  );
}

type ErrorListBannerProps = {
  delivery: Delivery;
  alert: Extract<DeliveryAlertState, { kind: 'errorList' }>;
  hasReadOnlyPermission: boolean;
};

function ErrorListBanner({ delivery, alert, hasReadOnlyPermission }: ErrorListBannerProps) {
  return (
    <>
      <Banner variant="error" title={alert.title} mb="200">
        {/* Error messages routinely carry recipient PII and rendered attribute values. */}
        <List gap="100" className="fs-mask">
          {alert.messages.map((message, index) => (
            <ListItem key={index}>{message}</ListItem>
          ))}
        </List>
      </Banner>

      <ErrorActionButtons delivery={delivery} hasReadOnlyPermission={hasReadOnlyPermission} />
    </>
  );
}

type ErrorActionButtonsProps = { delivery: Delivery; hasReadOnlyPermission: boolean };

function ErrorActionButtons({ delivery, hasReadOnlyPermission }: ErrorActionButtonsProps) {
  const workspaceId = requireWorkspaceId();
  const taxonomy = useProductTaxonomy();

  const peopleBase = taxonomy.route('people.index').replace(':workspaceId', String(workspaceId));
  const recipientLabel = isEmptyString(delivery.recipient) ? 'customer' : delivery.recipient;

  return (
    <ButtonGroup groupVariant="spaced">
      {hasDeliveryCustomer(delivery) ? (
        <Button
          variant="secondary"
          icon="person"
          isDisabled={hasReadOnlyPermission}
          href={`${peopleBase}/${delivery.customer_id}/attributes/edit`}
          className="fs-mask"
        >
          Add data to {recipientLabel}
        </Button>
      ) : null}

      {hasDeliveryAction(delivery) ? (
        <Button
          variant="secondary"
          icon="edit"
          isDisabled={hasReadOnlyPermission}
          href={generatePath('/workspaces/:workspaceId/journeys/composer/actions/:actionId', {
            workspaceId,
            actionId: delivery.action_id ?? 0,
          })}
        >
          Fix {deliveryChannelShortName(delivery)}
        </Button>
      ) : null}
    </ButtonGroup>
  );
}
